import api from '../../utils/api';
import { SectorItem, SectorPermission } from './settings.types';

const STORAGE_KEY = 'mc_sectors_config';

const DEFAULT_PERMISSIONS_ALL_TRUE: SectorPermission = {
  view: { pedido: true, produto: true, categoria: true, status: true, usuario: true, setor: true, configuracao: true, relatorios: true },
  add: { pedido: true, produto: true, categoria: true, status: true, usuario: true, setor: true, configuracao: true, relatorios: true },
  edit: { pedido: true, produto: true, categoria: true, status: true, usuario: true, setor: true, configuracao: true, relatorios: true },
  delete: { pedido: true, produto: true, categoria: true, status: true, usuario: true, setor: true, configuracao: true, relatorios: true },
  export: { pedido: true, produto: true, categoria: true, status: true, usuario: true, setor: true, configuracao: true, relatorios: true },
};

const DEFAULT_PERMISSIONS_NORMAL: SectorPermission = {
  view: { pedido: true, produto: true, categoria: true, status: true, usuario: false, setor: false, configuracao: false, relatorios: true },
  add: { pedido: true, produto: false, categoria: false, status: false, usuario: false, setor: false, configuracao: false, relatorios: false },
  edit: { pedido: true, produto: false, categoria: false, status: false, usuario: false, setor: false, configuracao: false, relatorios: false },
  delete: { pedido: false, produto: false, categoria: false, status: false, usuario: false, setor: false, configuracao: false, relatorios: false },
  export: { pedido: true, produto: false, categoria: false, status: false, usuario: false, setor: false, configuracao: false, relatorios: true },
};

const INITIAL_SECTORS: SectorItem[] = [
  {
    id: '01',
    name: 'Administração',
    permissions: JSON.parse(JSON.stringify(DEFAULT_PERMISSIONS_ALL_TRUE)),
  },
  {
    id: '02',
    name: 'Vendas',
    permissions: JSON.parse(JSON.stringify(DEFAULT_PERMISSIONS_NORMAL)),
  },
  {
    id: '03',
    name: 'Corte',
    permissions: {
      ...JSON.parse(JSON.stringify(DEFAULT_PERMISSIONS_NORMAL)),
      view: { pedido: true, produto: true, categoria: false, status: true, usuario: false, setor: false, configuracao: false, relatorios: false },
      edit: { pedido: true, produto: false, categoria: false, status: true, usuario: false, setor: false, configuracao: false, relatorios: false },
    },
  },
  {
    id: '04',
    name: 'Dobra',
    permissions: {
      ...JSON.parse(JSON.stringify(DEFAULT_PERMISSIONS_NORMAL)),
      view: { pedido: true, produto: true, categoria: false, status: true, usuario: false, setor: false, configuracao: false, relatorios: false },
      edit: { pedido: true, produto: false, categoria: false, status: true, usuario: false, setor: false, configuracao: false, relatorios: false },
    },
  },
  {
    id: '05',
    name: 'Acessórios',
    permissions: {
      ...JSON.parse(JSON.stringify(DEFAULT_PERMISSIONS_NORMAL)),
      view: { pedido: true, produto: true, categoria: true, status: true, usuario: false, setor: false, configuracao: false, relatorios: false },
    },
  },
];

export class SettingsController {
  static getSectors(): SectorItem[] {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (!data) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(INITIAL_SECTORS));
        return INITIAL_SECTORS;
      }
      const parsed = JSON.parse(data);
      if (Array.isArray(parsed)) {
        return parsed;
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(INITIAL_SECTORS));
      return INITIAL_SECTORS;
    } catch {
      return INITIAL_SECTORS;
    }
  }

  static async getSectorsFromServer(): Promise<SectorItem[]> {
    try {
      const response = await api.get('/system-settings?key=sectors_config');
      if (response.data && response.data.value) {
        const parsed = JSON.parse(response.data.value);
        if (Array.isArray(parsed)) {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
          return parsed;
        }
      }
    } catch (e) {
      console.warn("Could not load sectors from MySQL, falling back to local storage.", e);
    }
    const local = this.getSectors();
    // Tenta sincronizar se estiver vazio no banco
    try {
      await api.post('/system-settings', { key: 'sectors_config', value: local });
    } catch {}
    return local;
  }

  static saveSector(id: string | null, name: string, permissions: SectorPermission): SectorItem[] {
    const list = this.getSectors();
    if (id) {
      const index = list.findIndex(s => s.id === id);
      if (index !== -1) {
        list[index].name = name;
        list[index].permissions = permissions;
      }
    } else {
      const nextId = String(Math.max(...list.map(s => parseInt(s.id) || 0), 0) + 1).padStart(2, '0');
      list.push({
        id: nextId,
        name,
        permissions,
      });
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    return list;
  }

  static async saveSectorToServer(id: string | null, name: string, permissions: SectorPermission): Promise<SectorItem[]> {
    const list = await this.getSectorsFromServer();
    if (id) {
      const index = list.findIndex(s => s.id === id);
      if (index !== -1) {
        list[index].name = name;
        list[index].permissions = permissions;
      }
    } else {
      const nextId = String(Math.max(...list.map(s => parseInt(s.id) || 0), 0) + 1).padStart(2, '0');
      list.push({
        id: nextId,
        name,
        permissions,
      });
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    try {
      await api.post('/system-settings', { key: 'sectors_config', value: list });
    } catch (e) {
      console.error("Failed saving sector to MySQL server", e);
    }
    return list;
  }

  static deleteSector(id: string): SectorItem[] {
    let list = this.getSectors();
    list = list.filter(s => s.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    return list;
  }

  static async deleteSectorFromServer(id: string): Promise<SectorItem[]> {
    let list = await this.getSectorsFromServer();
    list = list.filter(s => s.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    try {
      await api.post('/system-settings', { key: 'sectors_config', value: list });
    } catch (e) {
      console.error("Failed deleting sector on MySQL server", e);
    }
    return list;
  }

  static getEmptyPermissions(): SectorPermission {
    return {
      view: { pedido: false, produto: false, categoria: false, status: false, usuario: false, setor: false, configuracao: false, relatorios: false },
      add: { pedido: false, produto: false, categoria: false, status: false, usuario: false, setor: false, configuracao: false, relatorios: false },
      edit: { pedido: false, produto: false, categoria: false, status: false, usuario: false, setor: false, configuracao: false, relatorios: false },
      delete: { pedido: false, produto: false, categoria: false, status: false, usuario: false, setor: false, configuracao: false, relatorios: false },
      export: { pedido: false, produto: false, categoria: false, status: false, usuario: false, setor: false, configuracao: false, relatorios: false },
    };
  }

  static getFullPermissions(): SectorPermission {
    return {
      view: { pedido: true, produto: true, categoria: true, status: true, usuario: true, setor: true, configuracao: true, relatorios: true },
      add: { pedido: true, produto: true, categoria: true, status: true, usuario: true, setor: true, configuracao: true, relatorios: true },
      edit: { pedido: true, produto: true, categoria: true, status: true, usuario: true, setor: true, configuracao: true, relatorios: true },
      delete: { pedido: true, produto: true, categoria: true, status: true, usuario: true, setor: true, configuracao: true, relatorios: true },
      export: { pedido: true, produto: true, categoria: true, status: true, usuario: true, setor: true, configuracao: true, relatorios: true },
    };
  }
}
